import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import type { Pickup, PickupSnapshot } from "../api/types";

/**
 * Server-side pickups for a project — the source of truth for which flags are
 * "picked" (★) and whether each is "addressed". Shared by the report (star +
 * tally), the project page (tally) and the Pickups Package. Returns the list, a
 * Set of picked flag_keys, and optimistic pick / unpick / setAddressed actions.
 *
 * Pass `live` on the Package so it refetches on focus + a light interval, so
 * picks made elsewhere (other tab / device) appear without a manual reload.
 */
const NO_PICKUPS: Pickup[] = [];

export function usePickups(projectId: string, live = false) {
  const qc = useQueryClient();
  const key = ["pickups", projectId];

  const base = `/api/modules/proofing/projects/${projectId}/pickups`;
  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: () => api<{ pickups: Pickup[] }>(base).then((r) => r.pickups),
    enabled: !!projectId,
    refetchOnWindowFocus: live,
    refetchInterval: live ? 8000 : false,
  });

  // The same list and Set until the pickups change. A new Set on every render
  // made the report rebuild its whole flag list each time anything redrew it.
  const list = data ?? NO_PICKUPS;
  const keys = useMemo(() => new Set(list.map((p) => p.flag_key)), [list]);

  const setCache = (fn: (cur: Pickup[]) => Pickup[]) =>
    qc.setQueryData<Pickup[]>(key, (cur) => fn(cur ?? []));
  const invalidate = () => qc.invalidateQueries({ queryKey: key });

  const pickM = useMutation({
    mutationFn: (snap: PickupSnapshot) =>
      api<Pickup>(base, { method: "POST", body: JSON.stringify(snap) }),
    // Upsert, not insert-if-absent. This mutation is how an EXISTING flag is
    // edited too — nudged ±1s, annotated — and returning `cur` unchanged in
    // that case meant every edit waited out the POST *and* the refetch before
    // anything moved. Nudging felt broken for exactly that reason.
    onMutate: (snap) =>
      setCache((cur) => {
        const at = cur.findIndex((p) => p.flag_key === snap.flag_key);
        if (at < 0) return [...cur, { ...snap, addressed: false, audio_url: null }];
        const next = cur.slice();
        // Keep the server-derived fields; the snapshot owns everything else.
        next[at] = { ...next[at], ...snap };
        return next;
      }),
    onSettled: invalidate,
  });

  const unpickM = useMutation({
    mutationFn: (flag_key: string) =>
      api(`${base}/remove`, { method: "POST", body: JSON.stringify({ flag_key }) }),
    onMutate: (flag_key) => setCache((cur) => cur.filter((p) => p.flag_key !== flag_key)),
    onSettled: invalidate,
  });

  const addressedM = useMutation({
    mutationFn: (v: { flag_key: string; addressed: boolean }) =>
      api(`${base}/addressed`, { method: "POST", body: JSON.stringify(v) }),
    onMutate: (v) =>
      setCache((cur) => cur.map((p) => (p.flag_key === v.flag_key ? { ...p, addressed: v.addressed } : p))),
    onSettled: invalidate,
  });

  return {
    list,
    keys,
    isLoading,
    // `opts` forwards per-call callbacks so a caller can tell whether the write
    // actually landed. The cache update is optimistic, so without this a failed
    // POST is silent: the row appears, `onSettled` refetches, and it vanishes
    // again with nothing said. Anything that CONFIRMS a write to the user has
    // to be able to take that back.
    pick: (snap: PickupSnapshot, opts?: { onError?: () => void; onSuccess?: () => void }) =>
      pickM.mutate(snap, opts),
    unpick: (flag_key: string) => unpickM.mutate(flag_key),
    setAddressed: (flag_key: string, addressed: boolean) => addressedM.mutate({ flag_key, addressed }),
  };
}

/**
 * Owner control for the project's public, no-login share link. Reads the current
 * state (enabled + token), and exposes `enable` (idempotent — mints a token) and
 * `revoke`. `url` is the absolute link to hand to a narrator/editor.
 */
export function useShareLink(projectId: string) {
  const qc = useQueryClient();
  const key = ["share-link", projectId];
  const base = `/api/modules/proofing/projects/${projectId}/pickups/share`;

  const { data } = useQuery({
    queryKey: key,
    queryFn: () => api<{ enabled: boolean; token: string | null }>(base),
    enabled: !!projectId,
  });

  const enableM = useMutation({
    mutationFn: () => api<{ enabled: boolean; token: string }>(base, { method: "POST" }),
    onSuccess: (d) => qc.setQueryData(key, d),
  });
  const revokeM = useMutation({
    // Turning sharing off KEEPS the token (same link can come back on), so trust
    // the server's response rather than blanking the token client-side.
    mutationFn: () => api<{ enabled: boolean; token: string | null }>(base, { method: "DELETE" }),
    onSuccess: (d) => qc.setQueryData(key, d),
  });

  const token = data?.token ?? null;
  const enabled = !!data?.enabled;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return {
    enabled,
    token,
    // The link exists once a token has ever been minted; `enabled` says whether
    // it currently works. The owner can copy the stable URL either way.
    url: token ? `${origin}/share/${token}` : null,
    enabling: enableM.isPending,
    enable: () => enableM.mutateAsync(),
    revoke: () => revokeM.mutate(),
  };
}
